import type { AgentTemplate } from '@reach/shared-types/agent-templates';
import { AgentTemplateKind, SYSTEM_TOOL_NAMES } from '@reach/shared-types/agents';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useToast } from '../../../hooks/useToast';
import {
  addAgentTemplateGrant,
  fetchActiveTools,
  fetchAgentTemplates,
  isVoiceSyncEnqueueFailure,
  removeAgentTemplateGrant,
  type GrantableTool,
} from './agent-templates-api';
import { TEMPLATES_COPY } from './agent-templates-copy';
import { buildAgentTemplateGrantKey, type AgentTemplateGrantTarget } from './grant-key';

export const AGENT_TEMPLATES_QUERY_KEY = ['agent-templates'] as const;
export const GRANTABLE_TOOLS_QUERY_KEY = ['agent-template-grantable-tools'] as const;

const TEMPLATE_ORDER = [AgentTemplateKind.SMS, AgentTemplateKind.VOICE] as const;
const SYSTEM_TOOL_NAME_SET: ReadonlySet<string> = new Set(SYSTEM_TOOL_NAMES);

const orderTemplatesForDisplay = (templates: AgentTemplate[]): AgentTemplate[] =>
  TEMPLATE_ORDER.flatMap(kind => templates.filter(template => template.kind === kind));

const toGrantableTools = (tools: GrantableTool[]): GrantableTool[] =>
  tools
    .filter(tool => !SYSTEM_TOOL_NAME_SET.has(tool.name))
    .sort((left, right) => left.display_name.localeCompare(right.display_name));

const CACHE_OPTIONS = {
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  refetchOnWindowFocus: true,
} as const;

type GrantChange =
  | {
      action: 'add';
      templateId: string;
      kind: AgentTemplateKind;
      target: AgentTemplateGrantTarget;
      key: string;
    }
  | { action: 'remove'; templateId: string; kind: AgentTemplateKind; grantId: string; key: string };

export function useAgentTemplates() {
  return useQuery({
    queryKey: AGENT_TEMPLATES_QUERY_KEY,
    queryFn: fetchAgentTemplates,
    select: orderTemplatesForDisplay,
    ...CACHE_OPTIONS,
  });
}

export function useGrantableTools() {
  return useQuery({
    queryKey: GRANTABLE_TOOLS_QUERY_KEY,
    queryFn: fetchActiveTools,
    select: toGrantableTools,
    ...CACHE_OPTIONS,
    staleTime: 0,
  });
}

export function useTemplateGrantMutation() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [pendingGrantKey, setPendingGrantKey] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const grantMutation = useMutation({
    mutationFn: async (change: GrantChange): Promise<void> => {
      if (change.action === 'add') {
        await addAgentTemplateGrant(change.templateId, change.target);
        return;
      }
      await removeAgentTemplateGrant(change.templateId, change.grantId);
    },
    onMutate: (change: GrantChange) => {
      setPendingGrantKey(change.key);
      setBanner(null);
    },
    onSuccess: (_result: void, change: GrantChange) => {
      const templateDrivesPhoneAgents = change.kind === AgentTemplateKind.VOICE;
      addToast({
        title: TEMPLATES_COPY.saved,
        description: templateDrivesPhoneAgents ? TEMPLATES_COPY.voiceSavedDetail : undefined,
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      const grantSavedButSyncUnqueued = isVoiceSyncEnqueueFailure(error);
      setBanner(
        grantSavedButSyncUnqueued
          ? TEMPLATES_COPY.syncQueueFailed
          : `${TEMPLATES_COPY.saveFailed} ${error.message}`,
      );
    },
    // A grant can commit and still fail, so the served state is only trustworthy after a refetch.
    onSettled: (_result, _error, change) => {
      void queryClient.invalidateQueries({ queryKey: AGENT_TEMPLATES_QUERY_KEY });
      setPendingGrantKey(pendingKey => (pendingKey === change.key ? null : pendingKey));
    },
  });

  const addGrant = (template: AgentTemplate, target: AgentTemplateGrantTarget) => {
    grantMutation.mutate({
      action: 'add',
      templateId: template.id,
      kind: template.kind,
      target,
      key: buildAgentTemplateGrantKey(template.id, target),
    });
  };

  const removeGrant = (template: AgentTemplate, grantId: string) => {
    const grant = template.grants.find(candidate => candidate.grantId === grantId);
    grantMutation.mutate({
      action: 'remove',
      templateId: template.id,
      kind: template.kind,
      grantId,
      key: buildAgentTemplateGrantKey(template.id, grant ?? { grantId }),
    });
  };

  return { pendingGrantKey, banner, addGrant, removeGrant };
}
